import { QuotingService } from '../../modules/shared/services/quoting.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../index';
import {
    quotingCheckTerms,
    quotingClose,
    quotingCompleted,
    quotingCompleteMulti,
    quotingFailed,
    quotingFailedMulti,
    quotingFocus,
    quotingPendingSave,
    quotingShowTerms,
    quotingStart,
    quotingStartMulti,
    quotingTermsAccepted,
    quotingTermsDeclined,
    quotingTermsRequired
} from './quote-progress.actions';
import { catchError, concatMap, exhaustMap, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ShipmentService } from '../../data/shipment.service';
import { Injectable } from '@angular/core';
import { selectShipmentEditorNeedsSave, selectShipmentEditorShipment, selectShipmentEditorSaving } from '../shipment-editor/shipment-editor.state';
import {
    shipmentEditorDisplayEditor,
    shipmentEditorSaveFailure,
    shipmentEditorSaveRequest,
    shipmentEditorSaveSuccess
} from '../shipment-editor/shipment-editor.actions';
import { selectQuotingPendingSave } from './quote-progress.state';
import { MessagingService } from '../../modules/shared/services/messaging.service';
import { LocaleService } from '../../modules/shared/services/locale.service';
import { of } from 'rxjs';
import { selectShipmentsTableSelectedHeaders } from '../shipments-table/shipments-table.state';
import { ShipmentState } from '../../utils/shipment-state';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TermsService } from '../../modules/shared/services/terms.service';
import { selectProfileState } from '../profile/profile.state';
import { MatDialog } from '@angular/material/dialog';
import { TermsAcceptanceModalComponent } from '../../modules/shared/terms-acceptance-modal/terms-acceptance-modal.component';
import { profileTermsAgreed } from '../profile/profile.actions';

@Injectable()
export class QuoteProgressEffects {

    constructor(private action$: Actions, private store: Store<AppState>, private quotingService: QuotingService,
                private shipmentService: ShipmentService, private snackbar: MatSnackBar, private messagingService: MessagingService,
                private localeService: LocaleService, private termsService: TermsService, private dialog: MatDialog) {
    }

    quotingStartRequestPending$ = createEffect(() => this.action$.pipe(
        ofType(quotingStart),
        tap((action) => this.shipmentService.cacheQuotes(action.id, [], null)),
        withLatestFrom(this.store.select(selectShipmentEditorNeedsSave)),
        filter(([ , needsSave ]) => needsSave),
        map(([ action, ]) => quotingPendingSave({ id: action.id }))
    ));

    quotingStartRequest$ = createEffect(() => this.action$.pipe(
        ofType(quotingStart),
        withLatestFrom(this.store.select(selectShipmentEditorNeedsSave)),
        filter(([ , needsSave ]) => !needsSave),
        map(([ action, ]) => quotingCheckTerms({ id: action.id }))
    ));

    quotingCheckTerms$ = createEffect(() => this.action$.pipe(
        ofType(quotingCheckTerms),
        withLatestFrom(
            this.store.select(selectShipmentEditorShipment),
            this.store.select(selectProfileState)
        ),
        switchMap(([ action, shipment, profileState ]) => {
            if (!shipment) {
                return of(quotingFailed({ id: action.id, messages: [ this.localeService.createServerMessage('Shipment not found') ] }));
            }

            const requiredTermTypes = this.termsService.termsRequiredToQuote(shipment);
            const outstandingTerms = profileState.profile.outstandingTerms;
            const requiredOutstandingTermIds = outstandingTerms
                .filter(term => requiredTermTypes.includes(term.type))
                .map(term => term.id);

            if (requiredOutstandingTermIds.length > 0) {
                return of(quotingTermsRequired({ id: action.id, termsIds: requiredOutstandingTermIds }));
            } else {
                // No terms required, proceed with actual quoting
                return this.quotingService.quoteShipmentSync(action.id).pipe(
                    tap((update) => this.shipmentService.cacheQuotes(action.id, update.quotes, update.preSelectedId)),
                    map((update) => quotingCompleted({ id: action.id, messages: update.messages, quotes: update.quotes })),
                    catchError((e) => of(quotingFailed({ id: action.id, messages: [ this.localeService.createServerMessage(e.message) ] })))
                );
            }
        })
    ));

    quotingStartMulti$ = createEffect(() => this.action$.pipe(
        ofType(quotingStartMulti),
        withLatestFrom(this.store.select(selectShipmentsTableSelectedHeaders)),
        map(([ , selection ]) => selection
            .filter(h => ShipmentState.isQuotable(h.status))
            .map(h => h.id)),
        exhaustMap((selection) => this.quotingService.quoteShipmentMulti(selection).pipe(
            map((results) => quotingCompleteMulti({ results })),
            catchError((e) => of(quotingFailedMulti({ ids: selection, messages: [ this.localeService.createServerMessage(e.message) ] })))
        ))
    ));

    quotingStart$ = createEffect(() => this.action$.pipe(
        ofType(quotingStart),
        map(() => quotingFocus({ id: null }))
    ));

    quotingPendingSave$ = createEffect(() => this.action$.pipe(
        ofType(quotingPendingSave),
        withLatestFrom(this.store.select(selectShipmentEditorSaving)),
        filter(([ , saving ]) => !saving),
        map((action) => shipmentEditorSaveRequest())
    ));

    quotingPendingReady = createEffect(() => this.action$.pipe(
        ofType(shipmentEditorSaveSuccess),
        withLatestFrom(this.store.select(selectQuotingPendingSave)),
        map(([ , id ]) => id),
        filter((id) => !!id),
        map((id) => quotingStart({ id }))
    ));

    quotingPendingFailure = createEffect(() => this.action$.pipe(
        ofType(shipmentEditorSaveFailure),
        withLatestFrom(this.store.select(selectQuotingPendingSave)),
        map(([ , id ]) => id),
        filter((id) => !!id),
        tap(() => this.snackbar.open($localize`Unable to save changes to the shipment prior to quoting.  Please try again.`)),
        map((id) => quotingClose({ id }))
    ));

    quotingTermsRequired$ = createEffect(() => this.action$.pipe(
        ofType(quotingTermsRequired),
        map((action) => {
            const firstTermId = action.termsIds[0];
            return quotingShowTerms({ id: action.id, termId: firstTermId });
        })
    ));

    quotingShowTerms$ = createEffect(() => this.action$.pipe(
        ofType(quotingShowTerms),
        withLatestFrom(this.store.select(selectProfileState)),
        concatMap(([ action, profileState ]) => {
            const term = profileState.profile.outstandingTerms.find(t => t.id === action.termId);
            
            if (!term) {
                // Term not found, skip to next or proceed with quoting
                return of(quotingStart({ id: action.id }));
            }

            // Fetch term text if not already loaded
            return (term.text ? of(term) : this.termsService.get(term.id)).pipe(
                switchMap(fullTerm => {
                    const dialogRef = this.dialog.open(TermsAcceptanceModalComponent, {
                        data: { term: fullTerm },
                        disableClose: true,
                        width: '600px',
                        autoFocus: false
                    });

                    return dialogRef.afterClosed().pipe(
                        map(accepted => {
                            if (accepted) {
                                return quotingTermsAccepted({ id: action.id, termId: action.termId });
                            } else {
                                return quotingTermsDeclined({ id: action.id });
                            }
                        })
                    );
                }),
                catchError(() => of(quotingTermsDeclined({ id: action.id })))
            );
        })
    ));

    quotingTermsAccepted$ = createEffect(() => this.action$.pipe(
        ofType(quotingTermsAccepted),
        concatMap((action) => 
            this.termsService.agree(action.termId).pipe(
                map(() => ({ success: true, action, error: null })),
                catchError((error) => {
                    console.error('Terms agreement failed for term:', action.termId, 'Error:', error);
                    this.snackbar.open($localize`Failed to record terms agreement. Please try again or contact support.`);
                    return of({ success: false, action, error });
                })
            )
        ),
        switchMap(({ success, action }) => {
            if (success) {
                this.store.dispatch(profileTermsAgreed({ id: action.termId }));
                return of(quotingStart({ id: action.id }));
            } else {
                return of(quotingClose({ id: action.id }));
            }
        })
    ));

    quotingTermsDeclined$ = createEffect(() => this.action$.pipe(
        ofType(quotingTermsDeclined),
        tap(() => {
            this.store.dispatch(shipmentEditorDisplayEditor());
        }),
        map((action) => quotingClose({ id: action.id }))
    ));

    // quotingCompletedMulti$ = createEffect(() => this.action$.pipe(
    //     ofType(quotingCompleteMulti),
    //     tap(action => action.results.map(
    //         r => this.shipmentService.getByKey(r.shipmentId, { mergeStrategy: MergeStrategy.OverwriteChanges })
    //             .subscribe(s => this.shipmentService.updateOneInCache(s))
    //     )),
    // ), { dispatch: false });

}
